/**
 * Skin Quiz Component
 * Interactive quiz for skincare routine recommendations
 */

if (!customElements.get("skin-quiz")) {
  class SkinQuiz extends HTMLElement {
    constructor() {
      super();
      this.stepIndex = 0;
    }

    connectedCallback() {
      this.form = this.querySelector("[data-skin-quiz-form]");
      this.steps = Array.from(this.querySelectorAll("[data-skin-quiz-step]"));
      this.progressBar = this.querySelector("[data-skin-quiz-progress-bar]");
      this.progressText = this.querySelector("[data-skin-quiz-progress-text]");
      this.result = this.querySelector("[data-skin-quiz-result]");
      this.resultTitle = this.querySelector("[data-skin-quiz-result-title]");
      this.resultBody = this.querySelector("[data-skin-quiz-result-body]");

      if (!this.form || !this.steps.length) return;

      this.totalSteps = this.steps.length;
      this.showStep(this.stepIndex);
      this.bindEvents();
    }

    bindEvents() {
      // Event delegation for all quiz interactions
      this.addEventListener("click", this.handleClick.bind(this));
      this.addEventListener("change", this.handleChange.bind(this));
    }

    handleClick(event) {
      const target = event.target;

      if (target.closest("[data-skin-quiz-next]")) {
        this.nextStep();
      } else if (target.closest("[data-skin-quiz-back]")) {
        this.prevStep();
      } else if (target.closest("[data-skin-quiz-finish]")) {
        this.finish();
      } else if (target.closest("[data-skin-quiz-restart]")) {
        this.restart();
      }
    }

    handleChange(event) {
      const target = event.target;

      if (target.type === "radio") {
        this.enableNavButton();
      }
    }

    updateProgress() {
      const current = this.stepIndex + 1;
      const percent = (current / this.totalSteps) * 100;

      if (this.progressBar) {
        this.progressBar.style.width = `${percent}%`;
      }

      if (this.progressText) {
        const template =
          this.progressText.dataset.template || "Vraag {current} van {total}";
        this.progressText.textContent = template
          .replace("{current}", current)
          .replace("{total}", this.totalSteps);
      }
    }

    showStep(index) {
      this.steps.forEach((step, i) => {
        step.hidden = i !== index;
      });

      if (this.result) {
        this.result.hidden = true;
      }

      this.updateProgress();
      this.enableNavButton();
    }

    enableNavButton() {
      const currentStep = this.steps[this.stepIndex];
      if (!currentStep) return;

      const navBtn = currentStep.querySelector(
        "[data-skin-quiz-next], [data-skin-quiz-finish]",
      );
      if (!navBtn) return;

      const radios = currentStep.querySelectorAll('input[type="radio"]');
      const hasAnswer = Array.from(radios).some((r) => r.checked);
      navBtn.disabled = !hasAnswer;
    }

    nextStep() {
      if (this.stepIndex < this.totalSteps - 1) {
        this.stepIndex++;
        this.showStep(this.stepIndex);
      }
    }

    prevStep() {
      if (this.stepIndex > 0) {
        this.stepIndex--;
        this.showStep(this.stepIndex);
      }
    }

    getAnswer(name) {
      const el = this.form.querySelector(`input[name="${name}"]:checked`);
      return el ? parseInt(el.value, 10) : null;
    }

    calculateOutcome() {
      const answers = {
        q1: this.getAnswer("q1"),
        q2: this.getAnswer("q2"),
        q3: this.getAnswer("q3"),
        q4: this.getAnswer("q4"),
        q5: this.getAnswer("q5"),
        q6: this.getAnswer("q6"),
        q7: this.getAnswer("q7"),
        q8: this.getAnswer("q8"),
      };

      // Outcome logic:
      // C = Sensitive/reactive (q1=4 OR q4=1)
      // A = Barrier-poor/dry (q1=1 OR q2=1)
      // B = Oily/congested (q1=3 AND q3=3)
      // D = Balanced (default)

      if (answers.q1 === 4 || answers.q4 === 1) return "C";
      if (answers.q1 === 1 || answers.q2 === 1) return "A";
      if (answers.q1 === 3 && answers.q3 === 3) return "B";
      return "D";
    }

    finish() {
      const outcome = this.calculateOutcome();
      this.renderOutcome(outcome);
      this.scrollToQuiz();
    }

    renderOutcome(code) {
      const template = this.querySelector(
        `template[data-skin-quiz-template="${code}"]`,
      );
      if (!template) return;

      // Hide all steps
      this.steps.forEach((s) => (s.hidden = true));

      // Update progress to complete
      if (this.progressBar) {
        this.progressBar.style.width = "100%";
      }
      if (this.progressText) {
        const doneText = this.progressText.dataset.doneText || "Klaar";
        this.progressText.textContent = doneText;
      }

      // Show result
      if (this.resultTitle) {
        this.resultTitle.textContent =
          this.resultTitle.dataset.title || "Jouw advies";
      }

      if (this.resultBody) {
        this.resultBody.innerHTML = "";
        this.resultBody.appendChild(template.content.cloneNode(true));
      }

      if (this.result) {
        this.result.hidden = false;
      }
    }

    restart() {
      if (this.form) {
        this.form.reset();
      }
      this.stepIndex = 0;
      this.showStep(this.stepIndex);
      this.scrollToQuiz();
    }

    scrollToQuiz() {
      const offset = 80;
      const rect = this.getBoundingClientRect();

      // Only scroll if the top of the container is outside the viewport
      if (rect.top < offset || rect.top > window.innerHeight) {
        const top = rect.top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  }

  customElements.define("skin-quiz", SkinQuiz);
}
